import styled, { useTheme, css } from 'foundations-theming/utils';
import { memo, useCallback } from 'react';
import { keyframes } from 'styled-components';
import I18n from 'I18n';
import { EARTH_LAYER } from 'foundations-components/transitional/constants';
import { NotificationBadge } from 'foundations-components/transitional/components/NotificationBadge';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { CommentsIcon } from 'foundations-assets';
import useButtonA11yProps from 'collaboration-sidebar/hooks/useButtonA11yProps';
import useUnreadRepliesCount from '../hooks/useUnreadRepliesCount';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BADGE_SIZE = 27;
const CommentIndicatorAnchor = styled.div.withConfig({
  displayName: "EmbeddedCommentIndicator__CommentIndicatorAnchor",
  componentId: "sc-1yxl0e7-0"
})([".hs-private.hs-private.hs-private &{position:relative;}.hs-private.hs-private.hs-private & .sr-only{position:absolute !important;width:1px !important;height:1px !important;margin:-1px !important;padding:0 !important;overflow:hidden !important;clip:rect(0,0,0,0) !important;border:0 !important;}"]);
const fadeIn = keyframes(["0%{opacity:0;}100%{opacity:1;}"]);
const HoverableIconCircle = styled.span.withConfig({
  displayName: "EmbeddedCommentIndicator__HoverableIconCircle",
  componentId: "sc-1yxl0e7-1"
})([".hs-private.hs-private.hs-private &{position:relative;display:flex;border-radius:50%;z-index:", ";opacity:0;animation:0.1s ", " 0s 1 ease-out forwards;transition:box-shadow ease-out 150ms;pointer-events:all;}.hs-private.hs-private.hs-private ", " &:hover{cursor:pointer;", "}"], EARTH_LAYER, fadeIn, ({
  borderColor,
  theme
}) => borderColor && `border-color: ${theme.color[borderColor] || borderColor};`, ({
  boxShadow,
  theme
}) => boxShadow && `box-shadow: 0 0 0 1px ${theme.color['border-interactive-pressed']}, 0 0 8px 0 ${theme.color['border-interactive-pressed']};`);
const CommentIndicatorPointer = ({
  fill = 'fill-brand-default'
}) => {
  const theme = useTheme();
  return /*#__PURE__*/_jsx("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 55.07 64.5",
    width: "100%",
    height: "100%",
    preserveAspectRatio: "none",
    children: /*#__PURE__*/_jsx("path", {
      d: "M18.652617 64.651013C40.374018 62.583472 56.004791 32.5 56.004791 32.5S40.374018 2.665303 18.652617.348987C-3.068785-1.967329 1.289013 32.5 1.289013 32.5s-4.357798 34.218554 17.363604 32.151013Z",
      fill: theme.color[fill] || fill || '#FF7A59',
      fillRule: "evenodd"
    })
  });
};
const SharedIconPointerStyled = css(["z-index:", ";position:absolute;inline-size:", "px;block-size:", "px;animation:0.1s ", " 0s 1 ease-out forwards;"], Number(EARTH_LAYER) - 1, BADGE_SIZE, BADGE_SIZE, fadeIn);
const HorizontalIconPointer = styled.span.attrs({
  ['data-test-id']: 'horizontal-pointer'
}).withConfig({
  displayName: "EmbeddedCommentIndicator__HorizontalIconPointer",
  componentId: "sc-1yxl0e7-2"
})([".hs-private.hs-private.hs-private &{", " inset-inline-start:6px;inset-block-start:0px;transform:scaleX(0.8);}.hs-private.hs-private.hs-private &:hover{cursor:pointer;}"], SharedIconPointerStyled);
const VerticalIconPointer = styled.span.attrs({
  ['data-test-id']: 'vertical-pointer'
}).withConfig({
  displayName: "EmbeddedCommentIndicator__VerticalIconPointer",
  componentId: "sc-1yxl0e7-3"
})([".hs-private.hs-private.hs-private &{", " transform:rotate(90deg) scaleX(0.8);inset-block-start:6px;inset-inline-start:0px;}.hs-private.hs-private.hs-private &:hover{cursor:pointer;}"], SharedIconPointerStyled);
const NotificationBadgePosition = styled.div.withConfig({
  displayName: "EmbeddedCommentIndicator__NotificationBadgePosition",
  componentId: "sc-1yxl0e7-4"
})([".hs-private.hs-private.hs-private &{position:absolute;inset-inline-end:-8px;inset-block-start:-10px;z-index:", ";}.hs-private.hs-private.hs-private &:hover{cursor:pointer;}"], EARTH_LAYER);
function EmbeddedCommentIndicator({
  collaborationSidebarState,
  isOpen,
  onClose,
  iconProps,
  renderCommentIndicator,
  setIsCommenting,
  verticalPositioning,
  hasCustomPosition,
  moduleParentComment,
  embeddedCommentWrapperRef,
  toggleOpenPopover
}) {
  const {
    unreadCommentsCount
  } = useUnreadRepliesCount({
    collaborationSidebarState,
    moduleParentComment
  });
  const onIconClick = useCallback(e => {
    if (isOpen && onClose) {
      onClose();
    } else {
      toggleOpenPopover(true);
      setIsCommenting(true);
      // Prevent the outside click event from closing the popover
      e.stopPropagation();
    }
  }, [isOpen, onClose, setIsCommenting, toggleOpenPopover]);
  const IconPointer = verticalPositioning ? VerticalIconPointer : HorizontalIconPointer;
  const buttonA11yProps = useButtonA11yProps({
    i18nKey: 'collaborationSidebar.modals.commentMode.viewComments',
    onClick: onIconClick
  });
  const isParentResolved = moduleParentComment === null || moduleParentComment === void 0 ? void 0 : moduleParentComment.resolved;
  const hoverableCircleBackgroundColor = isParentResolved ? 'icon-primary-disabled' : 'fill-brand-default';
  const notificationBadge = /*#__PURE__*/_jsx(NotificationBadge, {
    badgeLabel: unreadCommentsCount,
    badgeDescription: I18n.text('collaborationSidebar.modals.commentMode.newComments'),
    showBadge: unreadCommentsCount > 0,
    placement: "inline",
    children: /*#__PURE__*/_jsx("span", {})
  });
  if (renderCommentIndicator) {
    const renderPropOutput = renderCommentIndicator({
      buttonA11yProps,
      notificationBadge,
      onClick: onIconClick
    });
    if (renderPropOutput !== 'use-default') {
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(CommentIndicatorAnchor, {
          ref: embeddedCommentWrapperRef,
          children: renderPropOutput
        })
      );
    }
  }
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(CommentIndicatorAnchor, {
      ref: embeddedCommentWrapperRef,
      children: [/*#__PURE__*/_jsx(HoverableIconCircle, {
        id: "comment-indicator",
        "data-test-id": "comment-indicator",
        className: "comment-indicator",
        boxShadow: iconProps === null || iconProps === void 0 ? void 0 : iconProps.boxShadow,
        children: /*#__PURE__*/_jsx(IconCircle, Object.assign({
          icon: CommentsIcon,
          onClick: onIconClick,
          color: "text-core-on-fill-default",
          backgroundColor: hoverableCircleBackgroundColor,
          borderWidth: "0",
          blockSize: `${BADGE_SIZE}px`,
          inlineSize: `${BADGE_SIZE}px`,
          padding: "100"
        }, buttonA11yProps, iconProps))
      }), /*#__PURE__*/_jsx(NotificationBadgePosition, {
        id: "comment-indicator",
        onClick: onIconClick,
        children: notificationBadge
      }), !hasCustomPosition && /*#__PURE__*/_jsx(IconPointer, {
        id: "comment-indicator",
        onClick: onIconClick,
        children: /*#__PURE__*/_jsx(CommentIndicatorPointer, {
          fill: hoverableCircleBackgroundColor
        })
      })]
    })
  );
}
export default /*#__PURE__*/memo(EmbeddedCommentIndicator);